import apiService from "./api";
import { sanitizeQueryParams } from "../utility/helpers/apiHelper";
import type { SortOrderValue } from "@src/shared/constants/sort-order.constants";
import type { PaginationAndOrderParams } from "@src/shared/types/pagination.types";

export const CancellationPolicyStatus = {
  ACTIVE: "active",
  INACTIVE: "inactive",
} as const;

export type CancellationPolicyStatusValue =
  (typeof CancellationPolicyStatus)[keyof typeof CancellationPolicyStatus];

export interface CancellationPolicyRecord {
  id: string;
  businessId: number;
  businessName?: string;
  hoursBeforeSlot: number;
  refundPercentage: number;
  status: CancellationPolicyStatusValue;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
}

export interface CancellationPolicyFormPayload {
  businessId: number;
  hoursBeforeSlot: number;
  refundPercentage: number;
  status: CancellationPolicyStatusValue;
}

export interface GetCancellationPoliciesParams
  extends Partial<Omit<PaginationAndOrderParams, "page" | "pageSize">> {
  page?: number | string;
  pageSize?: number | string;
  search?: string;
  businessId?: string | number;
  status?: string;
  orderDirection?: SortOrderValue | Lowercase<SortOrderValue>;
}

export class CancellationPolicyService {
  async getCancellationPolicies(query?: GetCancellationPoliciesParams) {
    return apiService.get("/cancellation-policies", {
      params: sanitizeQueryParams(query || {}),
    });
  }

  async getCancellationPolicy(id: string) {
    return apiService.get(`/cancellation-policies/${id}`);
  }

  async createCancellationPolicy(data: CancellationPolicyFormPayload) {
    return apiService.post("/cancellation-policies", data);
  }

  async updateCancellationPolicy(
    id: string,
    data: Partial<CancellationPolicyFormPayload>
  ) {
    return apiService.put(`/cancellation-policies/${id}`, data);
  }

  async deleteCancellationPolicy(id: string) {
    return apiService.delete(`/cancellation-policies/${id}`);
  }
}

export const cancellationPolicyService = new CancellationPolicyService();
